import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PenetrationTesting() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Penetration Testing & Red Teaming Services | Synthera Security
        </title>

        <meta
          name="description"
          content="Expert penetration testing and red teaming services including web application testing, cloud security testing, network penetration testing, IoT security assessments, and AI penetration testing."
        />

        <meta
          name="keywords"
          content="Penetration Testing, Red Teaming, Web Application Security Testing, Cloud Penetration Testing, Network Security Testing, AI Penetration Testing, Cybersecurity Services, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Penetration Testing & Red Teaming Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="Identify vulnerabilities, validate defenses, and strengthen your security posture with expert penetration testing and red teaming services."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/penetration-testing"
        />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/penetration-testing"
        />

        <meta
          property="og:image"
          content="https://static.wixstatic.com/media/4f196e_d77c3671f4e9448cbebd755537d354dd~mv2.png"
        />
      </Helmet>

      <section className="bg-[#032d28] text-white relative overflow-hidden flex flex-col items-center py-24 px-6 min-h-screen">
        {/* BACK BUTTON */}
        <Link
          to="/services/security-consulting-advisory"
          className="absolute top-20 left-8 text-white/40 hover:text-[#24ff81] transition-colors"
        >
          <ArrowLeft size={26} />
        </Link>

        <div className="max-w-3xl mx-auto text-left w-full">
          {/* IMAGE */}
          <div className="flex justify-center mb-10">
            <img
              src="https://static.wixstatic.com/media/4f196e_d77c3671f4e9448cbebd755537d354dd~mv2.png"
              alt="Penetration Testing"
              className="w-44 h-44 rounded-full object-cover shadow-xl"
            />
          </div>

          {/* TITLE */}
          <h1 className="text-3xl md:text-4xl font-bold tracking-wide mb-6 uppercase text-center">
            Penetration Testing and Red Teaming
          </h1>

          {/* INTRO */}
          <p className="text-white/80 leading-relaxed mb-10">
            <b>Penetration testing</b> is the practice of simulating a
            cyberattack by an expert to identify vulnerabilities and test
            defences. Using the same tools, tactics and techniques as an
            adversary, a penetration test can be used to verify the efficacy of
            controls and to identify the potential impact of vulnerabilities to
            the organisation.
          </p>

          {/* BENEFITS */}
          <h2 className="text-xl font-bold mb-4">
            The <b>benefits</b> of good penetration testing are:
          </h2>

          <div className="space-y-4 text-white/80 mb-12">
            <p>
              <b>Visibility</b> of how vulnerabilities could be exploited to
              more effectively identify and implement security controls.
            </p>
            <p>
              <b>Gain real insight </b> into the potential impact of an attack,
              to better judge the risk of vulnerabilities in buildings, systems,
              applications or devices.
            </p>
            <p>
              <b>Clear, actionable advice</b> on how to remediate identified
              issues with advice from our expert consultants with real-world
              experience in fixing the same issues for other organisations.
            </p>
          </div>

          {/* APPROACH */}
          <h2 className="text-xl font-bold mb-4">Our Approach</h2>
          <p className="text-white/80 leading-relaxed mb-12">
            Our approach leverages both automated and manual tools and
            techniques, based on the latest threat intelligence, to provide a
            comprehensive view of your attack surface area. To ensure our
            findings are timely and accurate, we leverage industry standards
            such as the MITRE ATT&CK Framework and the OWASP Top 10. We believe
            that communication is important to any penetration test, including
            providing a concise understandable report, backed up by
            presentations to explain any findings and discuss remediation
            options.
          </p>

          {/* EXPERIENCE */}
          <h2 className="text-xl font-bold mb-4">Our Experience</h2>
          <p className="text-white/80 leading-relaxed mb-12">
            Our consultants have performed a wide range of penetration tests on
            applications, networks and entire organisations. They have developed
            their own tools to provide value and identify additional
            vulnerabilities in core areas, such as cloud penetration testing.
            Our consultants also hold a range of industry recognised
            certifications that demonstrates our enduring commitment to
            providing high quality penetration testing services, including OSCP
            and OSWP.
          </p>

          {/* KEY SERVICES */}
          <h2 className="text-xl font-bold mb-4">Key Services</h2>
          <p className="text-white/80 mb-4">
            Our most utilised penetration testing services are:
          </p>

          <ul className="list-disc pl-6 space-y-3 text-white/80 mb-14">
            <li>
              <b>Internal Penetration Testing</b>replicates an attacker with
              access to your internal network either a malicious insider or a
              threat that has gained access.
            </li>

            <li>
              <b>External Penetration Testing</b> is conducted remotely and
              replicates an Internet-based attacker attempting to gain access to
              your network.
            </li>

            <li>
              <b>Cloud Penetration Testing</b> simulates an attacker targeting
              your cloud environment, such as Amazon AWS, Microsoft Azure or
              Google GCP.
            </li>

            <li>
              <b>Web Application Penetration Testing</b> tests a web
              application, web service or API, replicating an attack based on a
              threat with various levels of access to your web application.
            </li>

            <li>
              <b>IOT Penetration Testing</b> is the identification, assessment
              and exploitation of various components present in an Internet of
              Things (IoT) device solution.
            </li>

            <li>
              <b>AI Penetration Testing</b> is the process of identifying and
              exploiting vulnerabilities using AI focused misuse cases.
            </li>
          </ul>

          {/* CTA */}
          <p className="text-white/80 mb-8">
            Get in touch today to learn how we can test your defences and
            strengthen your organisation’s security posture.
          </p>

          <div className="flex justify-center">
            <Link
              to="/contact"
              className="border border-white px-8 py-3 uppercase text-sm tracking-widest hover:bg-white hover:text-[#032d28] transition no-underline"
            >
              Find Out More
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
